import { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Trophy, TrendingUp } from "lucide-react";
import api from "../../lib/api";

/**
 * SalesAttributionCard — admin/super-admin view showing which consultant
 * closed how many leads-into-clients, split by package.
 * Non-admins see only their own row.
 */
export default function SalesAttributionCard({ isAdmin }) {
  const [data, setData] = useState(null);
  const [window, setWindow] = useState("month");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/crm/conversions/leaderboard", { params: { window } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [window]);

  const rows = data?.leaderboard || [];
  const totalConversions = rows.reduce((sum, r) => sum + r.total, 0);
  const totalFull = rows.reduce((sum, r) => sum + r.full_package, 0);
  const totalReg = rows.reduce((sum, r) => sum + r.registration_only, 0);

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="sales-attribution-card">
      <CardContent className="p-6">
        <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">
              {isAdmin ? "Sales attribution" : "My conversions"}
            </p>
            <h2 className="text-xl font-semibold tracking-tight text-[#0D1B2A]">
              Consultant leaderboard
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Who converted leads into paying clients, and which package they sold.
            </p>
          </div>
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger className="w-40" data-testid="attribution-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month" data-testid="window-month">This month</SelectItem>
              <SelectItem value="30d" data-testid="window-30d">Last 30 days</SelectItem>
              <SelectItem value="all" data-testid="window-all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Roll-up */}
        <div className="grid grid-cols-3 gap-3 mb-5">
          <RollUp icon={Trophy} label="Total conversions" value={totalConversions} tint="text-[#1A7F6E]" />
          <RollUp label="Full Package" value={totalFull} sub={totalConversions ? `${Math.round(totalFull / totalConversions * 100)}%` : "—"} />
          <RollUp label="Registration only" value={totalReg} sub={totalConversions ? `${Math.round(totalReg / totalConversions * 100)}%` : "—"} />
        </div>

        {loading ? (
          <p className="text-sm text-slate-500 text-center py-6">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
            <TrendingUp className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No conversions in this window yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="attribution-table">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left py-2 px-2">#</th>
                  <th className="text-left py-2 px-2">Consultant</th>
                  <th className="text-right py-2 px-2">Total</th>
                  <th className="text-right py-2 px-2">Full Package</th>
                  <th className="text-right py-2 px-2">Registration only</th>
                  <th className="text-left py-2 px-2 hidden md:table-cell">Last conversion</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.owner_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`attribution-row-${r.owner_id}`}>
                    <td className="py-2.5 px-2 text-slate-400">{i + 1}</td>
                    <td className="py-2.5 px-2">
                      <p className="font-medium text-[#0D1B2A]">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                    </td>
                    <td className="py-2.5 px-2 text-right font-semibold text-[#0D1B2A]">{r.total}</td>
                    <td className="py-2.5 px-2 text-right text-slate-700">{r.full_package}</td>
                    <td className="py-2.5 px-2 text-right text-slate-700">{r.registration_only}</td>
                    <td className="py-2.5 px-2 hidden md:table-cell text-xs text-slate-500">
                      {r.last_conversion_at ? new Date(r.last_conversion_at).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RollUp({ icon: Icon, label, value, sub, tint }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className={`w-3.5 h-3.5 ${tint || "text-slate-400"}`} />}
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</p>
      </div>
      <p className={`text-2xl font-semibold ${tint || "text-[#0D1B2A]"}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}
